import Vue from "vue";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import { DirectoryValue } from "@/modules/api/directory-value.model";
import dataStore from "@/store";

import OrderInformation from "../order-information.model";
import Plushie from "../plushie.model";
import PlushieImage from "../plushie-image.model";
import PlushieImageType from "../plushie-image-type.model";
import Product from "../product.model";
import Upgrade from "../upgrade.model";

const name = "PlushieStore";

if ((dataStore.state as any)[name]) {
  dataStore.unregisterModule(name);
}

@Module({ name, dynamic: true, store: dataStore })
export default class PlushieStore extends VuexModule {
  orderInformation: Dictionary<OrderInformation> = {};
  plushie: Dictionary<Plushie> = {};
  product: Dictionary<Product> = {};
  sellingStore: Dictionary<DirectoryValue> = {};
  upgrade: Dictionary<Upgrade> = {};
  image: Dictionary<PlushieImage> = {};

  orderIdOrderInformations: Dictionary<string[]> = {};
  plushieMainImage: Dictionary<string> = {};
  plushieImage: Dictionary<string[]> = {};
  plushieUpgrade: Dictionary<string[]> = {};

  // ################################### ORDER INFORMATION #########################################

  get getOrderInformationByPlushieId(): (
    id: string
  ) => OrderInformation | undefined {
    return (id: string) => this.orderInformation[id];
  }

  get getOrderInformationsByOrderId(): (orderId: string) => OrderInformation[] {
    return (orderId: string) => {
      const result: OrderInformation[] = [];

      this.orderIdOrderInformations[orderId].forEach((id) => {
        result.push(this.orderInformation[id]);
      });

      return result;
    };
  }

  get getOrderInformationsByOrderIds(): (
    orderIds: string[]
  ) => OrderInformation[] {
    return (orderIds: string[]) => {
      const result: OrderInformation[] = [];

      orderIds.forEach((orderId) => {
        if (!this.orderIdOrderInformations[orderId]) {
          return;
        }

        this.orderIdOrderInformations[orderId].forEach((id) => {
          result.push(this.orderInformation[id]);
        });
      });

      return result;
    };
  }

  @Mutation
  updateOrderInformation(payload: OrderInformation) {
    Vue.set(this.orderInformation, payload.id, payload);
  }

  @Mutation
  updateOrderIdOrderInformations({
    orderId,
    orderInformations,
  }: {
    orderId: string;
    orderInformations: OrderInformation[];
  }) {
    const orderInformationIds: string[] = [];
    orderInformations.forEach((item) => {
      orderInformationIds.push(item.id);
      Vue.set(this.orderInformation, item.id, item);
    });

    Vue.set(this.orderIdOrderInformations, orderId, orderInformationIds);
  }

  @Action({ rawError: true })
  async loadOrderInformationsByOrderId(orderId: string) {
    if (this.orderIdOrderInformations[orderId]) {
      const result: OrderInformation[] = [];

      this.orderIdOrderInformations[orderId].forEach((id) => {
        result.push(this.orderInformation[id]);
      });

      return result;
    }

    const collection = await DIContainer.OrderInformationRepository.getByOrderId(
      orderId
    );

    const orderInformations = collection.getItems();

    this.updateOrderIdOrderInformations({
      orderId,
      orderInformations,
    });

    return orderInformations;
  }

  @Action({ rawError: true })
  async loadOrderInformationsByOrderIds(orderIds: string[]) {
    const missing: string[] = [];
    const result: Dictionary<OrderInformation[]> = {};

    orderIds.forEach((orderId) => {
      if (!this.orderIdOrderInformations[orderId]) {
        missing.push(orderId);
        return;
      }

      result[orderId] = [];

      this.orderIdOrderInformations[orderId].forEach((id) => {
        result[orderId].push(this.orderInformation[id]);
      });
    });

    if (!missing.length) {
      return result;
    }

    const collection = await DIContainer.OrderInformationRepository.getByOrderIds(
      orderIds
    );

    const orderInformations = collection.getItems();

    const oderIdInformations: Dictionary<OrderInformation[]> = {};
    orderInformations.forEach((orderInformation) => {
      if (oderIdInformations[orderInformation.orderId] === undefined) {
        oderIdInformations[orderInformation.orderId] = [orderInformation];
        return;
      }

      oderIdInformations[orderInformation.orderId].push(orderInformation);
    });

    Object.keys(oderIdInformations).forEach((orderId) => {
      const items = oderIdInformations[orderId];

      this.updateOrderIdOrderInformations({
        orderId,
        orderInformations: items,
      });
    });

    orderInformations.forEach((orderInformation) => {
      if (!result[orderInformation.orderId]) {
        result[orderInformation.orderId] = [orderInformation];
        return;
      }

      result[orderInformation.orderId].push(orderInformation);
    });

    return result;
  }

  @Action({ rawError: true })
  async loadOrderInformationByPlushieId(id: string) {
    if (this.orderInformation[id]) {
      return this.orderInformation[id];
    }

    const orderInformation = await DIContainer.OrderInformationRepository.getById(
      id
    );

    if (orderInformation) {
      this.updateOrderInformation(orderInformation);
    }

    return orderInformation;
  }

  @Action({ rawError: true })
  async loadOrderInformationsByPlushieIds(ids: string[]) {
    const missing: string[] = [];
    const result: Dictionary<OrderInformation> = {};

    ids.forEach((id) => {
      if (!this.orderInformation[id]) {
        missing.push(id);
        return;
      }

      result[id] = this.orderInformation[id];
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.OrderInformationRepository.getByIds(ids);

    Object.keys(items).forEach((id) => {
      const item = items[id];

      if (!item) {
        return;
      }

      this.updateOrderInformation(item);

      result[item.id] = item;
    });

    return result;
  }

  // ################################### PLUSHIES #########################################

  get getPlushieById(): (id: string) => Plushie | undefined {
    return (id: string) => this.plushie[id];
  }

  get getPlushiesByIds(): (ids: string[]) => Plushie[] {
    return (ids: string[]) => {
      const plushies: Plushie[] = [];

      ids.forEach((id) => {
        if (this.plushie[id]) {
          plushies.push(this.plushie[id]);
        }
      });

      return plushies;
    };
  }

  @Mutation
  updatePlushie(payload: Plushie) {
    Vue.set(this.plushie, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadPlushiesByIds(ids: string[]) {
    const missing: string[] = [];
    let result: Dictionary<Plushie | undefined> = {};

    ids.forEach((id) => {
      if (!this.plushie[id]) {
        missing.push(id);
        return;
      }

      result[id] = this.plushie[id];
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.PlushieRepository.getByIds(missing);

    Object.keys(items).forEach((id) => {
      const item = items[id];
      if (!item) {
        return;
      }

      this.updatePlushie(item);
    });

    result = { ...result, ...items };

    return result;
  }

  @Action({ rawError: true })
  async loadPlushieById({
    id,
    useCache = true,
  }: {
    id: string;
    useCache?: boolean;
  }) {
    if (useCache && this.plushie[id]) {
      return this.plushie[id];
    }

    const plushie = await DIContainer.PlushieRepository.getById(id);

    if (!plushie) {
      return;
    }

    this.updatePlushie(plushie);

    return plushie;
  }

  @Action({ rawError: true })
  async loadPlushiesByStoreItemIds(
    ids: string[]
  ): Promise<Dictionary<Plushie>> {
    const items = await DIContainer.PlushieRepository.getByStoreItemIds(ids);

    Object.keys(items).forEach((storeItemId) => {
      const item = items[storeItemId];
      if (!item) {
        return;
      }

      this.updatePlushie(item);
    });

    return { ...items };
  }

  @Action({ rawError: true })
  async setPlushie(plushie: Plushie) {
    this.updatePlushie(plushie);

    return plushie;
  }

  @Action({ rawError: true })
  async refreshPlushie(plushieId: string) {
    return this.loadPlushieById({ id: plushieId, useCache: false });
  }

  // ################################### PLUSHIE IMAGES #########################################

  get getPlushieImageById(): (id: string) => PlushieImage | undefined {
    return (id: string) => this.image[id];
  }

  get getPlushieImagesByPlushieId(): (plushieId: string) => PlushieImage[] {
    return (plushieId: string) => {
      const imagesIds = this.plushieImage[plushieId];

      if (!imagesIds) {
        return [];
      }

      const result: PlushieImage[] = [];
      const additionalArtworks: PlushieImage[] = [];

      imagesIds.forEach((id) => {
        const image = this.image[id];
        if (!image) {
          return;
        }

        if (image.type === PlushieImageType.MAIN) {
          result.unshift(image);
        } else {
          additionalArtworks.push(image);
        }
      });

      additionalArtworks.sort((a, b) =>
        (a.createdAt || -1) > (b.createdAt || -1) ? 1 : -1
      );

      result.push(...additionalArtworks);

      return result;
    };
  }

  get getPlushieMainImageByPlushieId(): (
    plushieId: string
  ) => PlushieImage | undefined {
    return (plushieId: string) => {
      const imageId = this.plushieMainImage[plushieId];
      if (!imageId) {
        return undefined;
      }

      return this.image[imageId];
    };
  }

  @Mutation
  removePlushieImage(payload: PlushieImage) {
    const plushieRelations = this.plushieImage[payload.plushie];
    let index = -1;

    if (plushieRelations) {
      index = plushieRelations.indexOf(payload.id);
    }

    if (index !== -1) {
      plushieRelations.splice(index, 1);
    }

    if (payload.type === PlushieImageType.MAIN) {
      delete this.plushieMainImage[payload.plushie];
    }

    delete this.image[payload.id];
  }

  @Mutation
  updatePlushieImage(payload: PlushieImage) {
    Vue.set(this.image, payload.id, payload);

    if (
      this.plushieImage[payload.plushie] !== undefined &&
      !this.plushieImage[payload.plushie].includes(payload.id)
    ) {
      this.plushieImage[payload.plushie].push(payload.id);
    }

    if (payload.type === PlushieImageType.MAIN) {
      this.plushieMainImage[payload.plushie] = payload.id;
    }
  }

  @Mutation
  updatePlushieImages({
    plushieId,
    images,
  }: {
    plushieId: string;
    images: PlushieImage[];
  }) {
    images.forEach((image) => {
      if (image.plushie !== plushieId) {
        throw new Error("All images should belong to the specified plushie!");
      }
    });

    const plushieImages: string[] = [];

    images.forEach((image) => {
      plushieImages.push(image.id);
      Vue.set(this.image, image.id, image);

      if (image.type === PlushieImageType.MAIN) {
        this.plushieMainImage[image.plushie] = image.id;
      }
    });

    Vue.set(this.plushieImage, plushieId, plushieImages);
  }

  @Action({ rawError: true })
  async createPlushieImage({
    id,
    plushie,
    storageItem,
  }: {
    id: string;
    plushie: string;
    storageItem: string;
  }) {
    const item = await DIContainer.PlushieImageRepository.createArtwork(
      id,
      plushie,
      storageItem
    );

    this.updatePlushieImage(item);

    return item;
  }

  @Action({ rawError: true })
  async deletePlushieImage(image: PlushieImage): Promise<void> {
    await DIContainer.PlushieImageRepository.delete(image);

    this.removePlushieImage(image);

    return;
  }

  @Action({ rawError: true })
  async loadPlushieImagesByIds({
    ids,
    useCache = true,
  }: {
    ids: string[];
    useCache?: boolean;
  }): Promise<Dictionary<PlushieImage | undefined>> {
    const missing: string[] = [];
    let result: Dictionary<PlushieImage | undefined> = {};

    ids.forEach((id) => {
      if (!useCache || !this.image[id]) {
        missing.push(id);
        return;
      }

      result[id] = this.image[id];
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.PlushieImageRepository.getByIds(missing);

    Object.keys(items).forEach((id) => {
      const item = items[id];
      if (!item) {
        return;
      }

      this.updatePlushieImage(item);
    });

    result = { ...result, ...items };

    return result;
  }

  @Action({ rawError: true })
  async loadPlushieImageByPlushieId({
    plushieId,
    useCache = true,
  }: {
    plushieId: string;
    useCache?: boolean;
  }) {
    let images: PlushieImage[] = [];

    if (useCache && this.plushieImage[plushieId]) {
      const imagesIds = this.plushieImage[plushieId];

      imagesIds.forEach((id) => {
        images.push(this.image[id]);
      });

      return images;
    }

    images = await DIContainer.PlushieImageRepository.getByPlushieId(plushieId);

    this.updatePlushieImages({ plushieId, images });

    return images;
  }

  @Action({ rawError: true })
  async loadPlushieMainImageByPlushieIds(plushieIds: string[]) {
    const missing: string[] = [];
    const result: Dictionary<PlushieImage> = {};

    plushieIds.forEach((id) => {
      if (!this.plushieMainImage[id]) {
        missing.push(id);
        return;
      }

      const imageId = this.plushieMainImage[id];
      result[id] = this.image[imageId];
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.PlushieImageRepository.getMainByPlushieIds(
      missing
    );

    items.forEach((item) => {
      this.updatePlushieImage(item);
      result[item.plushie] = item;
    });

    return result;
  }

  @Action({ rawError: true })
  async setPlushieImageAsMain(image: PlushieImage): Promise<void> {
    const currentMainArtwork = this.plushieMainImage[image.plushie];

    if (currentMainArtwork == image.id) {
      return;
    }

    const images = await DIContainer.PlushieImageRepository.setAsMain(image);

    images.forEach((image) => {
      this.updatePlushieImage(image);
    });

    return;
  }

  // ################################### PLUSHIE UPGRADES #########################################

  get getPlushieUpgradesByPlushieId(): (plushieId: string) => string[] {
    return (plushieId: string) => {
      if (this.plushieUpgrade[plushieId] == null) {
        return [];
      }

      return [...this.plushieUpgrade[plushieId]];
    };
  }

  @Mutation
  updatePlushieUpgrades({
    plushieId,
    upgrades,
  }: {
    plushieId: string;
    upgrades: string[];
  }) {
    Vue.set(this.plushieUpgrade, plushieId, upgrades);
  }

  @Action({ rawError: true })
  async loadPlushieUpgradesByPlushieId({
    plushieId,
    useCache = true,
  }: {
    plushieId: string;
    useCache?: boolean;
  }) {
    if (useCache && this.plushieUpgrade[plushieId] != null) {
      return this.plushieUpgrade[plushieId];
    }

    const relations = await DIContainer.PlushieUpgradeRelationRepository.getByPlushieId(
      plushieId
    );

    const upgrades: string[] = [];

    relations.forEach((relation) => {
      upgrades.push(relation.upgrade);
    });

    this.updatePlushieUpgrades({ plushieId, upgrades });

    return upgrades;
  }

  @Action({ rawError: true })
  async loadPlushieUpgradesByPlushieIds({
    plushieIds,
    useCache = true,
  }: {
    plushieIds: string[];
    useCache?: boolean;
  }) {
    const missing: string[] = [];
    const result: Dictionary<string[]> = {};

    plushieIds.forEach((id) => {
      if (useCache && !this.plushieUpgrade[id]) {
        missing.push(id);
        return;
      }

      result[id] = this.plushieUpgrade[id];
    });

    if (!missing.length) {
      return result;
    }

    const relations = await DIContainer.PlushieUpgradeRelationRepository.getByPlushieIds(
      plushieIds
    );

    const upgrades: Dictionary<string[]> = {};

    relations.forEach((relation) => {
      if (!upgrades[relation.plushie]) {
        upgrades[relation.plushie] = [relation.upgrade];
        return;
      }

      upgrades[relation.plushie].push(relation.upgrade);
    });

    Object.keys(upgrades).forEach((plushieId) => {
      this.updatePlushieUpgrades({ plushieId, upgrades: upgrades[plushieId] });

      result[plushieId] = upgrades[plushieId];
    });

    return upgrades;
  }

  // ################################### PRODUCTS #########################################

  get getProductById(): (id: string) => Product | undefined {
    return (id: string) => this.product[id];
  }

  get productsList() {
    const list: Product[] = [];

    Object.keys(this.product).forEach((id) => {
      list.push(this.product[id]);
    });

    list.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    return list;
  }

  get productsDictionary(): Dictionary<DirectoryValue> {
    return { ...this.product };
  }

  @Mutation
  updateProduct(payload: Product) {
    Vue.set(this.product, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadProducts() {
    let items: Product[];

    if (Object.keys(this.product).length === 0) {
      const collection = await DIContainer.ProductRepository.getList();
      items = collection.getItems();

      items.forEach((item) => {
        this.updateProduct(item);
      });
    }

    items = [];

    Object.keys(this.product).forEach((key) => {
      items.push(this.product[key]);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadProductById(id: string) {
    await this.loadProducts();

    return this.getProductById(id);
  }

  // ################################### STORES #########################################

  get getStoreById(): (id: string) => DirectoryValue | undefined {
    return (id: string) => this.sellingStore[id];
  }

  @Mutation
  updateStore(payload: DirectoryValue) {
    Vue.set(this.sellingStore, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadStores() {
    let items: DirectoryValue[];

    if (Object.keys(this.sellingStore).length === 0) {
      const collection = await DIContainer.StoreRepository.getList();
      items = collection.getItems();

      items.forEach((item) => {
        this.updateStore(item);
      });
    }

    items = [];

    Object.keys(this.sellingStore).forEach((key) => {
      items.push(this.sellingStore[key]);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadStoreById(id: string) {
    await this.loadStores();

    return this.getStoreById(id);
  }

  // ################################### UPGRADES #########################################

  get getUpgradeById(): (id: string) => Upgrade | undefined {
    return (id: string) => this.upgrade[id];
  }

  get getUpgradesByIds(): (ids: string[]) => Upgrade[] {
    return (ids: string[]) => {
      const result: Upgrade[] = [];

      ids.forEach((id) => {
        if (this.upgrade[id]) {
          result.push(this.upgrade[id]);
        }
      });

      return result;
    };
  }

  @Mutation
  updateUpgrade(payload: Upgrade) {
    Vue.set(this.upgrade, payload.id, payload);
  }

  @Action({ rawError: true })
  async loadUpgrades() {
    let items: Upgrade[];

    if (Object.keys(this.upgrade).length === 0) {
      const collection = await DIContainer.UpgradeRepository.getList(1, 9999);
      items = collection.getItems();

      items.forEach((item) => {
        this.updateUpgrade(item);
      });
    }

    items = [];

    Object.keys(this.upgrade).forEach((key) => {
      items.push(this.upgrade[key]);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadUpgradeById(id: string) {
    await this.loadUpgrades();

    return this.getUpgradeById(id);
  }
}
